import { useCallback, useId, useRef, useState } from 'react';
import { ImageOff, ImageUp, Loader2, X } from 'lucide-react';
import { inputBase } from '@/components/ui/input';
import { useTenantParams } from '@/hooks/use-tenant-params';
import { locationApi } from '@/lib/api-paths';
import { cn } from '@/lib/utils';

// =====================================================================
// ImageUploadField — drag-drop + click-to-pick + URL paste, all
// feeding the same onChange(url) callback. Used by gallery, hero,
// testimonials.avatar, team.photo.
//
// Pipeline:
//   1. Owner drops/picks/pastes-URL.
//   2. If file: client-side resize via canvas → JPEG 0.85, ≤ 1600px
//      wide. Then POST multipart to the LOCATION-scoped upload route,
//      `/api/b/:businessId/l/:locationId/uploads` (the only one the server
//      registers since the tenancy rename — the old flat `/api/uploads`
//      404s). Both ids come from the canonical editor route, never from
//      props or storage. Server writes to R2 and returns the public URL.
//   3. onChange(url) fires. URL also reflected into the URL input for
//      "I want to see what was stored" transparency.
//
// Public URL is only available after the operator wires
// cdn.pageboss.com to the R2 bucket; until then the endpoint returns
// 503 and the widget surfaces a friendly "uploads not yet enabled"
// message — URL paste still works.
// =====================================================================

const MAX_BYTES_INPUT = 8 * 1024 * 1024; // 8 MB raw input before resize
const RESIZE_MAX_WIDTH = 1600;
const RESIZE_QUALITY = 0.85;
const ACCEPTED_MIME = 'image/jpeg,image/png,image/webp,image/avif,image/gif';

type UploadState =
  | { kind: 'idle' }
  | { kind: 'resizing' }
  | { kind: 'uploading'; progress: number }
  | { kind: 'error'; message: string };

interface ImageUploadFieldProps {
  label: string;
  url: string;
  onChange: (next: string) => void;
}

export function ImageUploadField({ label, url, onChange }: ImageUploadFieldProps) {
  const fileInputId = useId();
  const urlInputId = useId();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const xhrRef = useRef<XMLHttpRequest | null>(null);
  const { businessId, locationId } = useTenantParams();

  const [state, setState] = useState<UploadState>({ kind: 'idle' });
  const [isDragOver, setIsDragOver] = useState(false);
  const [imageLoadError, setImageLoadError] = useState(false);

  const showImage = url && !imageLoadError;
  const uploading = state.kind === 'resizing' || state.kind === 'uploading';

  const handleFile = useCallback(
    async (file: File) => {
      // Abort any in-flight upload.
      xhrRef.current?.abort();
      xhrRef.current = null;

      if (!businessId || !locationId) {
        // Rendered outside a canonical Location route: there is no tenant to
        // upload into, so refuse rather than guess one.
        setState({
          kind: 'error',
          message: 'Image upload isn\'t available here. Paste an image URL instead.',
        });
        return;
      }
      const endpoint = locationApi(businessId, locationId, 'uploads');

      if (file.size > MAX_BYTES_INPUT) {
        setState({
          kind: 'error',
          message: `${(file.size / 1024 / 1024).toFixed(1)} MB is too large. Try a smaller image.`,
        });
        return;
      }
      if (!file.type.startsWith('image/')) {
        setState({
          kind: 'error',
          message: 'That doesn\'t look like an image. Use JPG, PNG, WEBP, AVIF, or GIF.',
        });
        return;
      }

      setState({ kind: 'resizing' });
      let resized: Blob;
      try {
        resized = await resizeToJpeg(file, RESIZE_MAX_WIDTH, RESIZE_QUALITY);
      } catch (err) {
        setState({
          kind: 'error',
          message:
            err instanceof Error
              ? `Could not read the image: ${err.message}`
              : 'Could not read the image.',
        });
        return;
      }

      setState({ kind: 'uploading', progress: 0 });
      try {
        const result = await uploadBlob(endpoint, resized, file.name, (loaded, total) => {
          setState({
            kind: 'uploading',
            progress: total > 0 ? Math.round((loaded / total) * 100) : 0,
          });
        }, (xhr) => {
          xhrRef.current = xhr;
        });
        xhrRef.current = null;
        setImageLoadError(false);
        setState({ kind: 'idle' });
        onChange(result.url);
      } catch (err) {
        xhrRef.current = null;
        setState({
          kind: 'error',
          message:
            err instanceof Error
              ? err.message
              : 'Upload failed. Try again, or paste a URL instead.',
        });
      }
    },
    [onChange, businessId, locationId],
  );

  const onDrop: React.DragEventHandler<HTMLLabelElement> = (e) => {
    e.preventDefault();
    setIsDragOver(false);
    const file = e.dataTransfer.files?.[0];
    if (file) void handleFile(file);
  };

  const onDragOver: React.DragEventHandler<HTMLLabelElement> = (e) => {
    e.preventDefault();
    if (!isDragOver) setIsDragOver(true);
  };

  const onDragLeave: React.DragEventHandler<HTMLLabelElement> = (e) => {
    // Only clear when leaving the label element entirely.
    if (e.currentTarget === e.target) setIsDragOver(false);
  };

  const onFileSelected: React.ChangeEventHandler<HTMLInputElement> = (e) => {
    const file = e.target.files?.[0];
    if (file) void handleFile(file);
    // Reset so the same file can be re-picked.
    e.target.value = '';
  };

  const clearImage = () => {
    xhrRef.current?.abort();
    xhrRef.current = null;
    setImageLoadError(false);
    setState({ kind: 'idle' });
    onChange('');
  };

  return (
    <div className="flex flex-col gap-1.5">
      <label className="text-sm font-medium text-foreground" htmlFor={fileInputId}>
        {label}
      </label>

      {/* Drop zone OR preview */}
      <label
        htmlFor={fileInputId}
        onDrop={onDrop}
        onDragOver={onDragOver}
        onDragLeave={onDragLeave}
        className={cn(
          'relative block w-full',
          'rounded-[var(--radius)] border-2 border-dashed',
          'transition-colors duration-[var(--duration-fast)]',
          'cursor-pointer',
          isDragOver
            ? 'border-brand-500 bg-brand-50'
            : 'border-border-subtle bg-surface-sunken hover:border-border',
          showImage && 'border-solid border-border-subtle bg-card',
          uploading && 'cursor-progress',
        )}
      >
        <input
          id={fileInputId}
          ref={fileInputRef}
          type="file"
          accept={ACCEPTED_MIME}
          onChange={onFileSelected}
          className="sr-only"
          disabled={uploading}
        />

        {showImage && !uploading ? (
          <div className="relative">
            <img
              src={url}
              alt=""
              className="w-full h-32 object-cover rounded-[calc(var(--radius)-2px)]"
              onError={() => setImageLoadError(true)}
              onLoad={() => setImageLoadError(false)}
            />
            <button
              type="button"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                clearImage();
              }}
              aria-label="Remove image"
              className={cn(
                'absolute top-2 right-2',
                'inline-flex items-center justify-center',
                'h-7 w-7 rounded-full',
                'bg-card/95 backdrop-blur-sm border border-border',
                'text-text-secondary hover:text-danger-fg hover:bg-danger-bg',
                'shadow-sm transition-colors duration-[var(--duration-fast)]',
                'focus-visible:outline-2 focus-visible:outline-brand-500',
              )}
            >
              <X className="h-4 w-4" aria-hidden />
            </button>
          </div>
        ) : uploading ? (
          <div className="flex flex-col items-center justify-center h-32 gap-2 px-4 text-center">
            <Loader2 className="h-5 w-5 text-brand-600 animate-spin" aria-hidden />
            <span className="text-sm text-text-secondary">
              {state.kind === 'resizing'
                ? 'Preparing image…'
                : `Uploading… ${state.kind === 'uploading' ? state.progress : 0}%`}
            </span>
            {state.kind === 'uploading' ? (
              <div className="w-32 h-1 rounded-full bg-surface-sunken overflow-hidden">
                <div
                  className="h-full w-full origin-left rounded-full bg-brand-500 transition-transform duration-[var(--duration-base)]"
                  style={{ transform: `scaleX(${state.progress / 100})` }}
                  aria-hidden
                />
              </div>
            ) : null}
          </div>
        ) : imageLoadError && url ? (
          <div className="flex flex-col items-center justify-center h-32 gap-2 px-4 text-center">
            <ImageOff className="h-6 w-6 text-text-tertiary" aria-hidden />
            <span className="text-sm text-text-secondary">
              Couldn't load this image. Replace it below, or fix the URL.
            </span>
          </div>
        ) : (
          <div className="flex flex-col items-center justify-center h-32 gap-2 px-4 text-center">
            <ImageUp className="h-6 w-6 text-text-tertiary" aria-hidden />
            <span className="text-sm font-medium text-foreground">
              Drag an image here, or click to pick one
            </span>
            <span className="text-xs text-text-tertiary">
              JPG, PNG, WEBP, AVIF, GIF · resized to ~1600px on upload
            </span>
          </div>
        )}
      </label>

      {state.kind === 'error' ? (
        <p className="text-xs text-danger-fg" role="alert">
          {state.message}
        </p>
      ) : null}

      {/* URL input — primary fallback when no image is set; collapses
          to a "Show URL" details disclosure once one is present so it
          doesn't compete with the drop-zone preview. */}
      {showImage || imageLoadError ? (
        <details className="mt-1 group">
          <summary
            className={cn(
              'cursor-pointer list-none',
              'text-xs text-text-tertiary hover:text-text-secondary',
              'inline-flex items-center gap-1',
              'transition-colors duration-[var(--duration-fast)]',
            )}
          >
            <span className="group-open:hidden">Show image URL</span>
            <span className="hidden group-open:inline">Image URL</span>
          </summary>
          <input
            id={urlInputId}
            type="url"
            value={url}
            placeholder="https://..."
            onChange={(e) => {
              setImageLoadError(false);
              onChange(e.target.value);
            }}
            // ONE input recipe (T12/B2 → T14) + the inspector's compact height.
            className={cn(inputBase, 'mt-1 h-8 py-1')}
          />
        </details>
      ) : (
        <div className="mt-1 flex flex-col gap-1">
          <label htmlFor={urlInputId} className="text-xs text-text-tertiary">
            Or paste an image URL
          </label>
          <input
            id={urlInputId}
            type="url"
            value={url}
            placeholder="https://..."
            onChange={(e) => {
              setImageLoadError(false);
              onChange(e.target.value);
            }}
            className={cn(inputBase, 'h-8 py-1')}
          />
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------

async function resizeToJpeg(file: File, maxWidth: number, quality: number): Promise<Blob> {
  const objectUrl = URL.createObjectURL(file);
  try {
    const img = await loadImage(objectUrl);
    const scale = Math.min(1, maxWidth / img.naturalWidth);
    const w = Math.max(1, Math.round(img.naturalWidth * scale));
    const h = Math.max(1, Math.round(img.naturalHeight * scale));
    const canvas = document.createElement('canvas');
    canvas.width = w;
    canvas.height = h;
    const ctx = canvas.getContext('2d');
    if (!ctx) throw new Error('Canvas 2D context unavailable');
    ctx.drawImage(img, 0, 0, w, h);
    const blob = await new Promise<Blob | null>((resolve) =>
      canvas.toBlob((b) => resolve(b), 'image/jpeg', quality),
    );
    if (!blob) throw new Error('Browser could not encode the image');
    return blob;
  } finally {
    URL.revokeObjectURL(objectUrl);
  }
}

function loadImage(src: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error('Image failed to load'));
    img.src = src;
  });
}

interface UploadResult {
  url: string;
  key: string;
}

function uploadBlob(
  endpoint: string,
  blob: Blob,
  originalName: string,
  onProgress: (loaded: number, total: number) => void,
  registerXhr: (xhr: XMLHttpRequest) => void,
): Promise<UploadResult> {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    registerXhr(xhr);
    xhr.open('POST', endpoint);
    xhr.upload.onprogress = (e) => {
      if (e.lengthComputable) onProgress(e.loaded, e.total);
    };
    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        try {
          const result = JSON.parse(xhr.responseText) as UploadResult;
          resolve(result);
        } catch {
          reject(new Error('Server returned a malformed response.'));
        }
      } else if (xhr.status === 503) {
        reject(
          new Error(
            'Image uploads aren\'t turned on yet for this account. Paste a URL for now.',
          ),
        );
      } else if (xhr.status === 413 || xhr.status === 400) {
        try {
          const body = JSON.parse(xhr.responseText) as { message?: string };
          reject(new Error(body.message || 'Upload was rejected.'));
        } catch {
          reject(new Error('Upload was rejected.'));
        }
      } else if (xhr.status === 401) {
        reject(new Error('Sign in again — your session expired.'));
      } else if (xhr.status === 404) {
        // The tenancy resolver answers EVERY refusal (not a member, Location
        // outside your scope, role below manager) with the same 404.
        reject(new Error('You don\'t have permission to upload images for this location. Ask an owner or manager, or paste a URL.'));
      } else if (xhr.status === 403) {
        reject(new Error('The upload was refused. Reload the editor and try again.'));
      } else if (xhr.status === 429) {
        reject(new Error('Too many uploads in a short time. Wait a minute and try again.'));
      } else {
        reject(new Error(`Upload failed (HTTP ${xhr.status}).`));
      }
    };
    xhr.onerror = () => reject(new Error('Network error while uploading.'));
    xhr.onabort = () => reject(new Error('Upload cancelled.'));
    const form = new FormData();
    form.append('file', blob, originalName);
    xhr.send(form);
  });
}
